import axios from "axios";
import { useContext, useState } from "react";
import Fade from "react-reveal/Fade";
import AppContext from "../context";
import {
  returnShopifyProductPrice,
  saveCart,
  trimProductTitle,
  zipAndStoreFilesToFirebase,
} from "../util/helpers";
import variables from "../styles/export.module.scss";
import { readableColor } from "color2k";
import { FaMinus, FaPlus, FaShoppingCart, FaTrash } from "react-icons/fa";

const OrderSummary = () => {
  const [poNumber, setPoNumber] = useState(null);
  const [customerNotes, setCustomerNotes] = useState(null);
  const {
    handleSetOrderState,
    materials,
    increaseInsertQuantity,
    decreaseInsertQuantity,
    removeInsertFromCart,
    cart,
    cartTotalPrice,
    handleSetGlobalModal,
    triggerDimensionsUpdate,
    handleSetSelectedMaterial,
    handleSetSelectedBottomType,
    handleSetSelectedBottomColor,
    handleSetBottomRequired,
    setEditedItem,
    handleSetCurrentInsertDimensions,
  } = useContext(AppContext);

  const editInsert = (item) => {
    handleSetGlobalModal(
      <div className="common-modal">
        <div className="common-modal__content content">
          <h2>
            Edit <strong>{item.title}</strong>
          </h2>
          <p>
            Are you sure you want to edit this insert? Changes to your current
            insert will be lost.
          </p>
          <div className="buttons">
            <button
              className="button is-success"
              onClick={() => {
                setEditedItem(item);
                handleSetGlobalModal(null, { forceClose: true });
                window?.loadInsert?.(item.insertJson);
                handleSetSelectedMaterial(item.material);
                handleSetCurrentInsertDimensions(item.dimensions);
                handleSetBottomRequired(item.bottomRequired);
                const bottomRequiredInput =
                  document.getElementById("bottom-required");
                if (bottomRequiredInput)
                  bottomRequiredInput.checked = item.bottomRequired;
                if (item.bottomRequired) {
                  const bottomType = trimProductTitle(
                    item.bottom.bottomMaterial.node.title
                  ).toLowerCase();
                  handleSetSelectedBottomColor(item.bottom.color, bottomType);
                  handleSetSelectedBottomType(bottomType, item.bottom.color);
                }
                window?.checkBottomRequired();
                // Give some time for the material to load before triggering dimensions update
                setTimeout(() => {
                  triggerDimensionsUpdate();
                }, 500);
                handleSetOrderState("designer");
              }}
            >
              Yes
            </button>
            <button
              className="button is-danger"
              onClick={() => {
                handleSetGlobalModal(null, { forceClose: true });
              }}
            >
              No
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleCheckout = async () => {
    try {
      if (cart && cart.length > 0) {
        let redirect = window?.open();
        const bottomMaterials = materials.filter((material) =>
          material.node.tags.includes("bottom-material")
        );

        let hasSVGFileProducts = "false";
        if (cart.some((item) => item.baseProduct.node.productType === "SVG"))
          hasSVGFileProducts = "true";

        let lineItems = [];
        cart.forEach((item) => {
          switch (item.baseProduct.node.productType) {
            case "SVG":
              let listItems = [
                {
                  merchandiseId:
                    item.baseProduct.node.variants.edges[0].node.storefrontId,
                  quantity: item.quantity,
                  attributes: [{ key: "Insert Name", value: item.title }],
                },
              ];
              lineItems = [...lineItems, ...listItems];
              break;
            default: {
              let listItems;
              let bottomMaterial = null;
              if (item.bottomRequired && item.bottom.bottomMaterial)
                bottomMaterial = bottomMaterials.find(
                  (material) =>
                    material.node.id === item.bottom.bottomMaterial.node.id
                );
              if (item.isAdditionalProduct) {
                listItems = [
                  {
                    merchandiseId:
                      item.baseProduct.node.variants.edges[0].node.storefrontId,
                    quantity: item.quantity,
                  },
                ];
              } else {
                listItems = bottomMaterial
                  ? [
                      {
                        merchandiseId:
                          item.baseProduct.node.variants.edges[0].node
                            .storefrontId,
                        quantity: item.quantity,
                        attributes: [{ key: "Insert Name", value: item.title }],
                      },
                      {
                        merchandiseId:
                          bottomMaterial.node.variants.edges[0].node
                            .storefrontId,
                        quantity: parseInt(
                          item.quantity * item.bottom.area * 10000
                        ),
                        attributes: [
                          { key: "Insert Name", value: item.title },
                          {
                            key: "Bottom Material Color",
                            value: item.bottom.color || item.material.name,
                          },
                        ],
                      },
                      {
                        merchandiseId:
                          item.material.variants.edges[0].node.storefrontId,
                        quantity: parseInt(
                          item.quantity * item.sides.area * 10000
                        ),
                        attributes: [{ key: "Insert Name", value: item.title }],
                      },
                    ]
                  : [
                      {
                        merchandiseId:
                          item.baseProduct.node.variants.edges[0].node
                            .storefrontId,
                        quantity: item.quantity,
                        attributes: [{ key: "Insert Name", value: item.title }],
                      },
                      {
                        merchandiseId:
                          item.material.variants.edges[0].node.storefrontId,
                        quantity: parseInt(
                          item.quantity * item.sides.area * 10000
                        ),
                        attributes: [{ key: "Insert Name", value: item.title }],
                      },
                    ];
              }
              if (item.largeKnifeBlocks?.quantity > 0) {
                listItems = [
                  ...listItems,
                  {
                    merchandiseId:
                      item.largeKnifeBlocks.variants.edges[0].node.storefrontId,
                    quantity: item.largeKnifeBlocks.quantity,
                    attributes: [{ key: "Insert Name", value: item.title }],
                  },
                ];
              }
              if (item.smallKnifeBlocks?.quantity > 0) {
                listItems = [
                  ...listItems,
                  {
                    merchandiseId:
                      item.smallKnifeBlocks.variants.edges[0].node.storefrontId,
                    quantity: item.smallKnifeBlocks.quantity,
                    attributes: [{ key: "Insert Name", value: item.title }],
                  },
                ];
              }
              if (item.spiceRacks?.quantity > 0) {
                listItems = [
                  ...listItems,
                  {
                    merchandiseId:
                      item.spiceRacks.variants.edges[0].node.storefrontId,
                    quantity: item.spiceRacks.quantity,
                    attributes: [{ key: "Insert Name", value: item.title }],
                  },
                ];
              }

              lineItems = [...lineItems, ...listItems];
              break;
            }
          }
        });
        const firebaseKeyForZip = await zipAndStoreFilesToFirebase(cart);
        const result = await axios.post("/.netlify/functions/store/checkout", {
          lineItems,
          firebaseKeyForZip,
          hasSVGFileProducts,
          poNumber,
          customerNotes,
        });
        redirect.location.href = result.data.checkoutUrl;
        saveCart([]);
        if (typeof window !== "undefined")
          sessionStorage.removeItem("materials");
      }
    } catch (error) {}
  };

  return (
    <Fade duration={250}>
      <aside
        className="order-summary columns ml-0 mr-0 is-multiline"
        style={{
          backgroundColor: variables.menuBgColor,
          color: readableColor(variables.menuBgColor),
        }}
      >
        <div className="order-summary__column column is-12 is-flex is-justify-content-space-between is-align-items-flex-end">
          <p className="is-flex is-align-items-center is-size-4">
            <FaShoppingCart />
            <span className="ml-2">Your cart</span>
          </p>
          <div className="is-flex is-align-items-center">
            <span
              className="arrow has-text-primary"
              style={{ transform: "rotate(180deg)" }}
            ></span>
            <button
              className="button is-primary is-outlined ml-2"
              onClick={() => handleSetOrderState("designer")}
            >
              Back to design
            </button>
          </div>
        </div>
        <hr className="horizontal" />
        <div className="order-summary__column column pt-0 pb-0 is-12">
          <div className="order-summary__items">
            {cart.length > 0 &&
              cart.map((item) => (
                <div className="order-summary__items--item" key={item.id}>
                  <div className="columns mb-0 mr-0">
                    <div className="column pb-0 is-9">
                      <div
                        className="is-flex"
                        style={
                          item.isAdditionalProduct
                            ? {
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                width: "100%",
                              }
                            : { justifyContent: "flex-start" }
                        }
                      >
                        <p className="title is-size-6 mb-0">{item.title}</p>
                        {item.isAdditionalProduct && (
                          <p style={{ fontSize: "0.85rem" }}>
                            {<b>${(item.price / 100).toFixed(2)}</b>} each
                          </p>
                        )}
                      </div>
                      <div className="is-flex is-justify-content-flex-start">
                        {!item.isAdditionalProduct && (
                          <p className="has-text-weight-bold is-size-6">
                            {item.dimensions.width.in.integer}
                            {item.dimensions.width.in.fraction.numerator ===
                            0 ? (
                              ""
                            ) : (
                              <span className="is-size-7">
                                {" "}
                                <sup>
                                  {item.dimensions.width.in.fraction.numerator}
                                </sup>
                                /
                                <sub>
                                  {
                                    item.dimensions.width.in.fraction
                                      .denominator
                                  }
                                </sub>
                              </span>
                            )}
                            ″ x {item.dimensions.depth.in.integer}
                            {item.dimensions.depth.in.fraction.numerator ===
                            0 ? (
                              ""
                            ) : (
                              <span className="is-size-7">
                                <sup>
                                  {item.dimensions.depth.in.fraction.numerator}
                                </sup>
                                /
                                <sub>
                                  {
                                    item.dimensions.depth.in.fraction
                                      .denominator
                                  }
                                </sub>
                              </span>
                            )}
                            ″ x {item.dimensions.height.in.integer}{" "}
                            {item.dimensions.height.in.fraction.numerator ===
                            0 ? (
                              ""
                            ) : (
                              <span className="is-size-7">
                                <sup>
                                  {item.dimensions.height.in.fraction.numerator}
                                </sup>
                                /
                                <sub>
                                  {
                                    item.dimensions.height.in.fraction
                                      .denominator
                                  }
                                </sub>
                              </span>
                            )}
                            ″
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className="column p-0 mt-3 is-3 is-flex is-align-items-center is-justify-content-flex-end"
                      style={{ height: "max-content" }}
                    >
                      <button
                        className="is-clickable shopping-cart__items--item__edit-button mr-3"
                        disabled={!item.insertJson}
                        onClick={() => editInsert(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="shopping-cart__items--item__remove-button reset-button is-clickable"
                        onClick={() => removeInsertFromCart(item)}
                      >
                        <i>
                          <FaTrash style={{ width: "1rem", height: "1rem" }} />
                        </i>
                      </button>
                      <div
                        className="shopping-cart__items--item__quantity ml-2"
                        style={{ height: "max-content" }}
                      >
                        <button
                          className="reset-button is-clickable"
                          disabled={item.baseProduct.node.productType === "SVG"}
                          onClick={() => {
                            if (item.quantity > 0) {
                              decreaseInsertQuantity(item);
                            }
                          }}
                        >
                          <i>
                            <FaMinus />
                          </i>
                        </button>
                        <p className="pl-1 pr-1">{item.quantity}</p>
                        <button
                          className="reset-button is-clickable"
                          disabled={item.baseProduct.node.productType === "SVG"}
                          onClick={() => {
                            increaseInsertQuantity(item);
                          }}
                        >
                          <i>
                            <FaPlus />
                          </i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="order-summary__items--item--costs">
                    {item.baseProduct.node.productType === "SVG" && (
                      <div className="is-flex is-justify-content-space-between pt-2 mb-4">
                        <p>Base Price for Drawer Insert</p>
                        <p>{`$${(
                          returnShopifyProductPrice(item.baseProduct) / 100
                        ).toFixed(2)}`}</p>
                      </div>
                    )}
                    {item.baseProduct && !item?.isAdditionalProduct && (
                      <>
                        <div className="is-flex is-justify-content-space-between pt-2">
                          <p>Base Price for Drawer Insert</p>
                          <p>{`$${(
                            returnShopifyProductPrice(item.baseProduct) / 100
                          ).toFixed(2)}`}</p>
                        </div>
                        <div className="is-flex is-justify-content-space-between pt-2">
                          <p>{`Dividers (${item.material.name})`}</p>
                          <p>{`$${(item.sides.sidesPrice / 100).toFixed(
                            2
                          )}`}</p>
                        </div>
                        {item.bottomRequired && (
                          <div className="is-flex is-justify-content-space-between pt-2">
                            <p>{item.bottom.bottomMaterial.node.title}</p>
                            <p>{`$${(item.bottom.price / 100).toFixed(2)}`}</p>
                          </div>
                        )}
                        {(item.largeKnifeBlocks?.quantity > 0 ||
                          item.smallKnifeBlocks?.quantity > 0 ||
                          item.spiceRacks?.quantity > 0) && (
                          <div className="mt-2">
                            <p>
                              <b>Additions</b>
                            </p>
                            {item.largeKnifeBlocks?.quantity > 0 && (
                              <div className="is-flex is-justify-content-space-between">
                                <div className="is-flex is-flex-direction-column">
                                  <p>
                                    {item.largeKnifeBlocks.title}
                                    {item.largeKnifeBlocks.quantity > 1 &&
                                      ` (Quantity: ${item.largeKnifeBlocks.quantity}) `}
                                  </p>
                                  {item.largeKnifeBlocks.quantity > 1 && (
                                    <p style={{ fontSize: "0.85rem" }}>
                                      {
                                        <b>
                                          $
                                          {(
                                            parseFloat(
                                              item.largeKnifeBlocks.price
                                            ) / 100
                                          ).toFixed(2)}
                                        </b>
                                      }{" "}
                                      each
                                    </p>
                                  )}
                                </div>
                                <p>{`$${(
                                  (item.largeKnifeBlocks.quantity *
                                    item.largeKnifeBlocks.price) /
                                  100
                                ).toFixed(2)}`}</p>
                              </div>
                            )}
                            {item.smallKnifeBlocks?.quantity > 0 && (
                              <div className="is-flex is-justify-content-space-between">
                                <div className="is-flex is-flex-direction-column">
                                  <p>
                                    {item.smallKnifeBlocks.title}
                                    {item.smallKnifeBlocks.quantity > 1 &&
                                      ` (Quantity: ${item.smallKnifeBlocks.quantity}) `}
                                  </p>
                                  {item.smallKnifeBlocks.quantity > 1 && (
                                    <p style={{ fontSize: "0.85rem" }}>
                                      {
                                        <b>
                                          $
                                          {(
                                            parseFloat(
                                              item.smallKnifeBlocks.price
                                            ) / 100
                                          ).toFixed(2)}
                                        </b>
                                      }{" "}
                                      each
                                    </p>
                                  )}
                                </div>
                                <p>{`$${(
                                  (item.smallKnifeBlocks.quantity *
                                    item.smallKnifeBlocks.price) /
                                  100
                                ).toFixed(2)}`}</p>
                              </div>
                            )}
                            {item.spiceRacks?.quantity > 0 && (
                              <div className="is-flex is-justify-content-space-between">
                                <div className="is-flex is-flex-direction-column">
                                  <p>
                                    {item.spiceRacks.title}
                                    {item.spiceRacks.quantity > 1 &&
                                      ` (Quantity: ${item.spiceRacks.quantity}) `}
                                  </p>
                                  {item.spiceRacks.quantity > 1 && (
                                    <p style={{ fontSize: "0.85rem" }}>
                                      {
                                        <b>
                                          $
                                          {(
                                            parseFloat(item.spiceRacks.price) /
                                            100
                                          ).toFixed(2)}
                                        </b>
                                      }{" "}
                                      each
                                    </p>
                                  )}
                                </div>
                                <p className="is-flex is-align-items-flex-end">{`$${(
                                  (item.spiceRacks.quantity *
                                    item.spiceRacks.price) /
                                  100
                                ).toFixed(2)}`}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="is-flex is-justify-content-space-between pt-3">
                    <p>Price</p>
                    <strong
                      style={{
                        color: readableColor(variables.menuBgColor),
                      }}
                    >{`$${
                      item.isAdditionalProduct
                        ? (
                            (item.quantity * parseFloat(item.price)) /
                            100
                          ).toFixed(2)
                        : (item.price / 100).toFixed(2)
                    }`}</strong>
                  </div>
                </div>
              ))}
          </div>
          {cart.length > 0 && (
            <hr
              className="horizontal mt-2"
              style={{ backgroundColor: readableColor(variables.menuBgColor) }}
            ></hr>
          )}

          <form className="pt-4 pb-5">
            <div className="field">
              <label
                className="label"
                style={{
                  color: readableColor(variables.menuBgColor),
                }}
              >{`P.O # (Optional)`}</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="Enter P.O #..."
                />
              </div>
            </div>
            <div className="field">
              <label
                className="label"
                style={{
                  color: readableColor(variables.menuBgColor),
                }}
              >{`Notes (Optional)`}</label>
              <div className="control">
                <textarea
                  className="textarea"
                  rows="3"
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="Enter notes for order..."
                />
              </div>
            </div>
          </form>
        </div>
        <hr
          className="horizontal mt-2"
          style={{ backgroundColor: readableColor(variables.menuBgColor) }}
        ></hr>
        <div className="column is-full mb-3">
          <p
            style={{
              color: readableColor(variables.menuBgColor),
            }}
          >
            All orders are shipped from Canada. Please note that shipments
            crossing the U.S. border may be subject to import duties, tariffs,
            or taxes charged by U.S. Customs. These charges are the
            responsibility of the customer and are not included in our product
            prices or shipping rates.
          </p>
        </div>
        <div
          className="order-summary__column columns is-multiline ml-0 mr-0 mb-0 p-2 pb-5"
          style={{ width: "100%", borderTop: "2px solid #b4b4c5" }}
        >
          <div className="column is-8 is-flex is-flex-direction-column is-justify-content-space-between pt-4 pb-0">
            {cart.length > 0 ? (
              <>
                <p
                  style={{
                    color: readableColor(variables.menuBgColor),
                  }}
                >
                  Subtotal (
                  {`${cart
                    .map((item) => item.quantity)
                    .reduce((a, b) => a + b, 0)} items`}
                  ):
                </p>
                <strong
                  className="is-size-3 mt-2"
                  style={{
                    lineHeight: "1",
                    color: readableColor(variables.menuBgColor),
                  }}
                >{`$${cartTotalPrice.toFixed(2)}`}</strong>
              </>
            ) : (
              <p className="is-size-3">
                <b>Cart is empty</b>
              </p>
            )}
          </div>
          <div className="column is-4 is-flex is-align-items-flex-end pb-0">
            {" "}
            <button
              className="button is-primary"
              style={{ maxWidth: "unset", width: "100%" }}
              disabled={cart.length === 0}
              onClick={() => handleCheckout()}
            >
              Checkout
            </button>
          </div>
        </div>
      </aside>
    </Fade>
  );
};

export default OrderSummary;
